import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-[#0b2545]/20 bg-[#faf8f5] text-sm text-[#0b2545] focus:outline-none focus:border-[#d4af37] transition-colors";
const labelCls = "block text-xs uppercase font-mono-df tracking-wider text-[#0b2545] font-semibold mb-1.5";

export default function ProductForm({ open, onOpenChange, categories, product, onSaved }) {
  const isEdit = Boolean(product?.id);
  const [form, setForm] = useState(() => ({
    name: product?.name || "",
    category: product?.category || categories[0]?.slug || "spezie",
    format: product?.format || "",
    box_qty: product?.box_qty || "",
    description: product?.description || "",
  }));
  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(product?.image_url ? `${API.replace("/api", "")}${product.image_url}` : null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setImagePath(data.path);
      setImagePreview(`${API.replace("/api", "")}${data.url}`);
      toast.success("Foto caricata e ridimensionata a 800×800");
    } catch (err) {
      toast.error("Upload fallito", { description: err?.response?.data?.detail || "Riprova con un'altra immagine." });
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, image_path: imagePath };
      if (isEdit) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      toast.success(isEdit ? "Prodotto aggiornato" : "Prodotto aggiunto");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error("Salvataggio fallito", { description: err?.response?.data?.detail || "Controlla i campi." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="max-w-lg bg-[#faf8f5] border-[#0b2545]/15 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0b2545] font-normal">
            {isEdit ? "Modifica Prodotto" : "Nuovo Prodotto"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSave} className="space-y-4 pt-2">
          <div>
            <label className={labelCls}>Nome prodotto *</label>
            <input data-testid="product-form-name" required minLength={2} className={inputCls} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Categoria *</label>
              <select data-testid="product-form-category" className={inputCls} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Formato / Peso</label>
              <input data-testid="product-form-format" placeholder="es. 500 g" className={inputCls} value={form.format}
                onChange={(e) => setForm({ ...form, format: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Pezzi per box</label>
              <input data-testid="product-form-box" placeholder="es. 12" className={inputCls} value={form.box_qty}
                onChange={(e) => setForm({ ...form, box_qty: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Descrizione</label>
            <textarea data-testid="product-form-description" rows={2} className={inputCls} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Foto prodotto</label>
            <label
              data-testid="product-form-image-upload"
              className="flex items-center gap-4 p-4 rounded-xl border-2 border-dashed border-[#0b2545]/20 bg-white cursor-pointer hover:border-[#d4af37] transition-colors"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Anteprima" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <span className="w-16 h-16 rounded-lg bg-[#f0ebe1] flex items-center justify-center text-[#0b2545]/40">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                </span>
              )}
              <span className="text-xs text-[#0b2545]/70">
                {uploading ? "Caricamento…" : imagePreview ? "Foto caricata — clicca per sostituirla" : "Clicca per caricare (verrà ridimensionata in automatico a 800×800)"}
              </span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
            </label>
          </div>
          <button
            data-testid="product-form-save"
            type="submit"
            disabled={saving || uploading}
            className="w-full py-3.5 rounded-xl bg-[#0b2545] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#d4af37] hover:text-[#06152d] transition-colors disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : isEdit ? "Salva Modifiche" : "Aggiungi Prodotto"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
